import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import moment from "moment";

import { v4 as uuidv4 } from "uuid";
import { calcularVencimientoPorPeriodo } from "../utils/calcvencimiento";
import { UrlApi } from "../../../services/apirest";
import { postData, putData } from "../../../services/library";
import { useReload } from "../context/ReloadContext";
import { useToast } from "../../../Hooks/UseToast";
export function useTareas(selectedEquipo) {
  const { triggerReload } = useReload();
  const { showToast, Toast } = useToast();
  // Estados para manejar las tareas y su formulario
  const [frecuencias, setFrecuencias] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [tareas, setTareas] = useState([]);
  const [mostrarInactivas, setMostrarInactivas] = useState(false);
  const [originalAlertaMaterial, setOriginalAlertaMaterial] = useState(null);
  const currentUser = localStorage.getItem("user") ?? "";

  useEffect(() => {
    axios
      .get(UrlApi + "/mp_tareas/frecuencias")
      .then((res) => setFrecuencias(res.data));
  }, []);

  const getTarea = useCallback(async () => {
    if (!selectedEquipo?.id) return;
    try {
      const { data } = await axios.get(
        UrlApi + "/mp_tareas/" + selectedEquipo.id
      );
      if (!data) {
        return; // Si no tiene ningun dato retorna nada para no pasar al catch
      }
      const tareasConVencimiento = data?.map((tarea) => {
        const baseFecha = tarea?.fecha_realizado || tarea?.ultimo;
        const fechaVencimiento = calcularVencimientoPorPeriodo(
          baseFecha,
          tarea
        );
        return {
          ...tarea,
          vencimiento: fechaVencimiento.format("YYYY-MM-DD"),
        };
      });
      setTareas(tareasConVencimiento);
    } catch (error) {
      showToast("Error al cargar los Datos", "error");
    }
  }, [selectedEquipo]);

  useEffect(() => {
    getTarea();
  }, [getTarea]);

  const tareasFiltradas = useMemo(
    () =>
      tareas.filter((t) =>
        mostrarInactivas ? t.activa === 0 : t.activa === 1
      ),
    [tareas, mostrarInactivas]
  );

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;
    setDatosForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };
  // Formulario por defecto para crear o editar una tarea
  const defaultForm = {
    id_frecuencia: "",
    tarea: "",
    materiales: "",
    herramientas: "",
    cert_especial: false,
    activa: 1,
    instructivohsse: false,
    responsable: "",
    ultimo: "",
    alerta_material: "",
    uuid_documentacion_tarea: uuidv4(),
  };

  const [DatosForm, setDatosForm] = useState(defaultForm);

  // Maneja la edición de una tarea seleccionada
  /**
   * Maneja la acción de edición para una fila dada.
   * Activa el modo de edición y llena los datos del formulario con los valores de la fila seleccionada.
   * Asegura que el campo 'uuid_documentacion_tarea' siempre esté definido, generando un nuevo UUID si es necesario.
   *
   * @param {Object} row - El objeto de datos que representa la fila a editar.
   * @param {string} [row.uuid_documentacion_tarea] - El UUID de la documentación de la tarea (opcional).
   * @returns {void}
   */
  const handleEdit = (row) => {
    setEditMode(true);
    setOriginalAlertaMaterial(row.alerta_material ?? null);
    setDatosForm({
      ...defaultForm,
      ...row,
      uuid_documentacion_tarea: row.uuid_documentacion_tarea ?? uuidv4(),
    });
  };
  const onGuardar = async (e) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);

      //Payload para crear o actualizar la tarea esto es lo que se enviará al servidor
      const payload = {
        id_frecuencia: DatosForm.id_frecuencia,
        tarea: DatosForm.tarea,
        materiales: DatosForm.materiales,
        herramientas: DatosForm.herramientas,
        cert_especial: DatosForm.cert_especial,
        activa: DatosForm.activa ? 1 : 0,
        instructivohsse: DatosForm.instructivohsse,
        responsable: DatosForm.responsable,
        ultimo: moment(DatosForm.ultimo).format("YYYY-MM-DD"),
        alerta_material: DatosForm.alerta_material
          ? moment(DatosForm.alerta_material).format("YYYY-MM-DD")
          : null,
        uuid_documentacion_tarea: DatosForm.uuid_documentacion_tarea,
        id_equipo: selectedEquipo.id,
      };

      if (editMode) {
        await axios.put(UrlApi + `/mp_tareas/${DatosForm.id}`, payload);
        showToast("Se ha actualizado la tarea correctamente", "success");
      } else {
        await axios.post(UrlApi + "/mp_tareas/", payload);
        showToast("Se ha creado la tarea correctamente", "success");
      }

      // Notificar al usuario si la alerta de materiales es nueva o cambió
      const alertaEsNueva = !editMode && DatosForm.alerta_material;
      const alertaCambio =
        editMode &&
        DatosForm.alerta_material &&
        DatosForm.alerta_material !== originalAlertaMaterial;

      if (
        (alertaEsNueva || alertaCambio) &&
        currentUser &&
        moment(DatosForm.alerta_material).isAfter(moment())
      ) {
        axios
          .post(UrlApi + "/mensajes/", {
            emisor: "sistema",
            receptor: currentUser,
            referencia: `Alerta de materiales – ${selectedEquipo?.equipo ?? "equipo"}`,
            mensaje: `La tarea "${DatosForm.tarea}" requiere materiales el ${moment(DatosForm.alerta_material).format("DD/MM/YYYY")}.\n\nMateriales necesarios: ${DatosForm.materiales}`,
          })
          .catch(() => {});
      }

      setDatosForm({ ...defaultForm, uuid_documentacion_tarea: uuidv4() });
      setOriginalAlertaMaterial(null);
      setEditMode(false);
      triggerReload();
      await getTarea();
    } catch (error) {
      showToast("Algo ha fallado", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const putActiva = async (row) => {
    if (!row.id) return;
    await axios.put(UrlApi + "/mp_tareas/" + row.id, {
      activa: row.activa === 1 ? 0 : 1,
    });
    await getTarea();
  };

  const onEliminar = async (row) => {
    if (!row.id) return;
    try {
      await axios.delete(UrlApi + "/mp_tareas/" + row.id);
      showToast("Se ha eliminado la tarea", "info");
      await getTarea();
    } catch (error) {
      showToast("Algo fallo al eliminar la tarea", "error");
    }
  };

  const resolverAlerta = async (tarea) => {
    if (!tarea?.id) return;
    try {
      await axios.put(UrlApi + `/mp_tareas/${tarea.id}`, { alerta_material: null });
      showToast("Alerta de materiales marcada como resuelta", "success");
      triggerReload();
      await getTarea();
    } catch {
      showToast("Error al resolver la alerta", "error");
    }
  };

  return {
    DatosForm,
    setDatosForm,
    frecuencias,
    isSubmitting,
    editMode,
    setEditMode,
    tareasFiltradas,
    mostrarInactivas,
    setMostrarInactivas,
    handleChange,
    handleEdit,
    onGuardar,
    putActiva,
    onEliminar,
    resolverAlerta,
    Toast,
  };
}
